import { GroupedSelectGroup } from '@/components/Form/GroupedSelect';

import {
  NoTaxResidencyReasonCode,
  TaxEntryType,
  TaxInfoEntry,
  TaxInfoRequestPayload,
} from '@/types/api/tax-info';
import { TranslatorFunction } from '@/types/i18n';

import {
  QUESTION_2_TAX_ENTRY_TYPES,
  replaceOwnedTaxEntries,
} from '../utils/taxInfoStatus';
import {
  ForeignNonTaxResidencyBlockValues,
  OtherCountriesTaxFormValues,
} from '../utils/types';
import { NO_TAX_RESIDENCY_REASON_CODES } from './constants';

export function isNoTaxResidencyReasonCode(
  code?: string,
): code is NoTaxResidencyReasonCode {
  return Object.values(NoTaxResidencyReasonCode).includes(
    code as NoTaxResidencyReasonCode,
  );
}

export function getForeignNonTaxResidencyReasonGroupedSelectConfig(
  t: TranslatorFunction,
) {
  const groups: GroupedSelectGroup[] = [
    {
      id: 'noTaxResidencyReason',
      options: NO_TAX_RESIDENCY_REASON_CODES.map(code => ({
        value: code,
        label: t(
          `taxInfoDrawer.question2.foreignNonTaxResidency.reason.options.${code}`,
        ),
      })),
    },
  ];

  return {
    groups,
    otherOption: {
      value: NoTaxResidencyReasonCode.OTHER,
      label: t(
        `taxInfoDrawer.question2.foreignNonTaxResidency.reason.options.${NoTaxResidencyReasonCode.OTHER}`,
      ),
    },
  };
}

export const DEFAULT_FOREIGN_NON_TAX_RESIDENCY_BLOCK: ForeignNonTaxResidencyBlockValues =
  {
    countryCode: '',
    noTaxResidencyReasonCode: '',
    noTaxResidencyReasonOther: '',
  };

export function getForeignNonTaxResidencyEntries(taxInfo?: TaxInfoEntry[]) {
  return (
    taxInfo?.filter(
      entry => entry.taxEntryType === TaxEntryType.FOREIGN_NON_TAX_RESIDENCY,
    ) ?? []
  );
}

export function mapTaxInfoEntryToForeignNonTaxResidencyBlock(
  entry: TaxInfoEntry,
): ForeignNonTaxResidencyBlockValues {
  return {
    countryCode: entry.countryCode ?? '',
    noTaxResidencyReasonCode: isNoTaxResidencyReasonCode(
      entry.noTaxResidencyReasonCode,
    )
      ? entry.noTaxResidencyReasonCode
      : '',
    noTaxResidencyReasonOther: entry.noTaxResidencyReasonOther ?? '',
  };
}

function buildForeignNonTaxResidencyEntry(
  block: ForeignNonTaxResidencyBlockValues,
): TaxInfoEntry {
  const entry: TaxInfoEntry = {
    countryCode: block.countryCode,
    taxEntryType: TaxEntryType.FOREIGN_NON_TAX_RESIDENCY,
    isCompleted: true,
    noTaxResidencyReasonCode: block.noTaxResidencyReasonCode || undefined,
  };

  if (block.noTaxResidencyReasonCode === NoTaxResidencyReasonCode.OTHER) {
    entry.noTaxResidencyReasonOther =
      block.noTaxResidencyReasonOther || undefined;
  }

  return entry;
}

export function buildForeignNonTaxResidencyPayload(
  formValues: OtherCountriesTaxFormValues,
  existingTaxInfo?: TaxInfoEntry[],
): TaxInfoRequestPayload {
  const nonTaxResidencyEntries = (
    formValues.foreignNonTaxResidencies ?? []
  ).map(buildForeignNonTaxResidencyEntry);
  return replaceOwnedTaxEntries(existingTaxInfo ?? [], nonTaxResidencyEntries, [
    ...QUESTION_2_TAX_ENTRY_TYPES,
  ]);
}

export function shouldShowForeignNonTaxResidencyForm(
  formValues: OtherCountriesTaxFormValues,
): boolean {
  return (
    formValues.hasForeignIndicia === true &&
    formValues.isForeignTaxResident === false
  );
}
